/* eslint-disable class-methods-use-this */

class BottleSceneController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;

    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.controls = null;
    this.initialized = false;
    this.initialFrontCameraDistance = null;

    this.bodyMaterial = null;
    this.capLogoMaterial = null;
    this.capSolidMaterials = [];

    this.textureLoader = new window.THREE.TextureLoader();
    this.textureLoader.setCrossOrigin('anonymous');
  }

  showLoading() {
    const { loadingElement } = this.orchestrator.dom;
    if (!loadingElement) return;
    loadingElement.classList.remove('is-hidden');
  }

  hideLoading() {
    const { loadingElement } = this.orchestrator.dom;
    if (!loadingElement) return;
    loadingElement.classList.add('is-hidden');
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  syncZoomFromCamera() {
    if (
      !this.camera ||
      !this.controls ||
      this.initialFrontCameraDistance == null
    ) return;
    const distance = this.camera.position.distanceTo(this.controls.target);
    if (distance > 0) {
      this.orchestrator.selectionState.zoom =
        this.initialFrontCameraDistance / distance;
    }
  }

  getCanvasSize() {
    const { clientHeight } = this.orchestrator.dom.deviceContent;
    return Math.max(Math.floor(clientHeight), 1);
  }

  resizeRenderer() {
    if (!this.renderer || !this.camera) return;

    const size = this.getCanvasSize();
    this.orchestrator.dom.deviceContent.style.setProperty(
      '--canvas-size',
      `${size}px`,
    );
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(size, size);
    this.render();
  }

  frameModel(object) {
    const box = new window.THREE.Box3().setFromObject(object);
    const size = box.getSize(new window.THREE.Vector3());
    const center = box.getCenter(new window.THREE.Vector3());

    object.position.sub(center);

    const maxDimension = Math.max(size.x, size.y, size.z);
    const fieldOfViewInRadians = (this.camera.fov * Math.PI) / 180;
    const distance =
      (maxDimension / 2 / Math.tan(fieldOfViewInRadians / 2)) * 1.55;

    this.camera.position.set(0, 0, distance);
    this.camera.near = distance / 100;
    this.camera.far = distance * 100;
    this.camera.updateProjectionMatrix();
    this.initialFrontCameraDistance = distance;
    this.orchestrator.selectionState.zoom = 1;

    if (this.controls) {
      this.controls.minDistance = distance * 0.22;
      this.controls.maxDistance = distance * 1.75;
    }

    return distance;
  }

  resetCameraToFrontView() {
    if (!this.camera || this.initialFrontCameraDistance == null) return;

    const distance = this.initialFrontCameraDistance;
    this.orchestrator.selectionState.zoom = 1;
    this.camera.position.set(0, 0, distance);
    this.camera.up.set(0, 1, 0);
    this.camera.near = distance / 100;
    this.camera.far = distance * 100;
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();

    if (this.controls) {
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    }
  }

  captureFrontPreviewImageDataUrl() {
    if (!this.renderer || !this.scene || !this.camera) return null;
    if (this.initialFrontCameraDistance == null) return null;

    const cameraPosition = this.camera.position.clone();
    const cameraUp = this.camera.up.clone();
    const cameraQuaternion = this.camera.quaternion.clone();
    const controlsTarget = this.controls?.target.clone() || null;

    this.resetCameraToFrontView();
    this.renderer.render(this.scene, this.camera);

    const exportSize = this.orchestrator.FINISH_PREVIEW_EXPORT_SIZE;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = exportSize;
    exportCanvas.height = exportSize;
    const exportContext = exportCanvas.getContext('2d');
    exportContext.drawImage(
      this.renderer.domElement,
      0,
      0,
      exportSize,
      exportSize,
    );
    const imageDataUrl = exportCanvas.toDataURL('image/png');

    this.camera.position.copy(cameraPosition);
    this.camera.up.copy(cameraUp);
    this.camera.quaternion.copy(cameraQuaternion);
    this.camera.updateProjectionMatrix();
    if (this.controls && controlsTarget) {
      this.controls.target.copy(controlsTarget);
      this.controls.update();
    }
    this.render();

    return imageDataUrl;
  }

  copyMapSettings(texture, reference) {
    if (!reference) return;
    texture.flipY = reference.flipY;
    texture.wrapS = reference.wrapS;
    texture.wrapT = reference.wrapT;
    texture.colorSpace = reference.colorSpace;
    texture.channel = reference.channel;
    texture.repeat.copy(reference.repeat);
    texture.offset.copy(reference.offset);
    texture.center.copy(reference.center);
    texture.rotation = reference.rotation;
  }

  configureCanvasTexture(texture) {
    texture.colorSpace = window.THREE.SRGBColorSpace;
    texture.magFilter = window.THREE.LinearFilter;
    texture.minFilter = window.THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    if (this.renderer) {
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    }
  }

  setMaterialMap(material, url, token) {
    const { selectionState } = this.orchestrator;

    return new Promise(resolve => {
      if (!material) {
        resolve();
        return;
      }

      const applyTexture = texture => {
        if (token !== selectionState.applyToken) {
          resolve();
          return;
        }
        this.copyMapSettings(texture, material.userData.originalMap);
        material.map = texture;
        material.needsUpdate = true;
        this.render();
        resolve();
      };

      if (selectionState.textureCache.has(url)) {
        applyTexture(selectionState.textureCache.get(url));
        return;
      }

      this.textureLoader.load(
        url,
        texture => {
          selectionState.textureCache.set(url, texture);
          applyTexture(texture);
        },
        undefined,
        () => resolve(),
      );
    });
  }

  makeSolidTexture(rgb, reference) {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 4;
    const context = canvas.getContext('2d');
    context.fillStyle = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    context.fillRect(0, 0, 4, 4);

    const texture = new window.THREE.CanvasTexture(canvas);
    this.copyMapSettings(texture, reference);
    this.configureCanvasTexture(texture);
    return texture;
  }

  applySelection() {
    const { selectionState, bodyTextureComposer } = this.orchestrator;

    selectionState.applyToken += 1;
    const token = selectionState.applyToken;
    const color = selectionState.getSelectedColor();
    const art = selectionState.getSelectedArt();
    const bodyCacheKey = bodyTextureComposer.getBodyBaseCacheKey(color, art);

    const needsFetch =
      !selectionState.bodyBaseCache.has(bodyCacheKey) ||
      !selectionState.textureCache.has(color.cap);

    if (needsFetch) this.showLoading();

    const bodyReady = bodyTextureComposer
      .buildBodyTexture(color, art, this.bodyMaterial?.userData.originalMap)
      .then(texture => {
        if (token !== selectionState.applyToken || !this.bodyMaterial) return;
        this.bodyMaterial.map = texture;
        this.bodyMaterial.needsUpdate = true;
      })
      .catch(error => {
        console.error(error);
      });

    const capReady = this.setMaterialMap(
      this.capLogoMaterial,
      color.cap,
      token,
    );

    if (token === selectionState.applyToken) {
      this.capSolidMaterials.forEach(material => {
        material.map = this.makeSolidTexture(
          color.solid,
          material.userData.originalMap,
        );
        material.needsUpdate = true;
      });
    }

    return Promise.all([bodyReady, capReady]).then(() => {
      if (token !== selectionState.applyToken) return;
      this.render();
      this.hideLoading();
    });
  }

  collectMaterials(root) {
    const { BODY_MATERIAL, CAP_LOGO_MATERIAL, CAP_SOLID_MATERIALS } =
      this.orchestrator;
    const materialsByName = {};

    root.traverse(child => {
      if (child.isMesh && child.material && child.material.name) {
        const { material } = child;
        if (!materialsByName[material.name]) {
          if (material.map) material.userData.originalMap = material.map;
          materialsByName[material.name] = material;
        }
      }
    });

    this.bodyMaterial = materialsByName[BODY_MATERIAL] || null;
    this.capLogoMaterial = materialsByName[CAP_LOGO_MATERIAL] || null;
    this.capSolidMaterials = CAP_SOLID_MATERIALS.map(
      name => materialsByName[name],
    ).filter(Boolean);

    if (this.bodyMaterial) {
      this.bodyMaterial.metalness = 0;
      this.bodyMaterial.roughness = 0.9;
      this.bodyMaterial.envMapIntensity = 0.35;
      this.bodyMaterial.metalnessMap = null;
      this.bodyMaterial.roughnessMap = null;
      this.bodyMaterial.needsUpdate = true;
    }
  }

  initializeSceneOnFirstOpen() {
    if (this.initialized) return;
    this.initialized = true;

    const { dom, selectionState, artsPanelController } = this.orchestrator;

    this.scene = new window.THREE.Scene();

    const size = this.getCanvasSize();
    dom.deviceContent.style.setProperty('--canvas-size', `${size}px`);

    this.camera = new window.THREE.PerspectiveCamera(45, 1, 0.1, 1000);
    this.camera.position.set(0, 0, 5);

    this.renderer = new window.THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(size, size);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.toneMapping = window.THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 1;
    dom.deviceContent.appendChild(this.renderer.domElement);

    const pmremGenerator = new window.THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmremGenerator.fromScene(
      new window.RoomEnvironment(),
      0.04,
    ).texture;

    this.controls = new window.OrbitControls(this.camera, this.renderer.domElement);
    this.controls.addEventListener('change', () => {
      this.syncZoomFromCamera();
      this.render();
    });

    window.addEventListener('resize', () => this.resizeRenderer());
    const resizeObserver = new ResizeObserver(() => this.resizeRenderer());
    resizeObserver.observe(dom.deviceContent);

    const loader = new window.GLTFLoader();
    loader.load(
      this.orchestrator.file360Url,
      gltf => {
        this.scene.add(gltf.scene);
        this.frameModel(gltf.scene);
        this.controls.target.set(0, 0, 0);
        this.controls.update();

        this.collectMaterials(gltf.scene);
        artsPanelController.buildArtsPanel();

        const defaultColorButton =
          dom.customizerMain.querySelector(
            '.color-swatch.active:not(:disabled)',
          ) || dom.customizerMain.querySelector('.color-swatch:not(:disabled)');
        selectionState.selectedColorKey =
          defaultColorButton?.dataset.color || 'rosa';

        this.applySelection().then(() => {
          this.resizeRenderer();
          this.render();
        });
      },
      undefined,
      error => {
        console.error(error);
        this.hideLoading();
      },
    );
  }

  zoomPreview(step) {
    if (
      !this.camera ||
      !this.controls ||
      this.initialFrontCameraDistance == null
    ) return;
    const { selectionState } = this.orchestrator;
    const direction = new window.THREE.Vector3().subVectors(
      this.camera.position,
      this.controls.target,
    );
    const currentDistance = direction.length();
    const currentZoom = currentDistance > 0
      ? this.initialFrontCameraDistance / currentDistance
      : 1;
    selectionState.zoom = Math.min(1.8, Math.max(0.8, currentZoom + step));
    const clampedDistance = this.initialFrontCameraDistance / selectionState.zoom;

    direction.normalize().multiplyScalar(clampedDistance);
    this.camera.position.copy(this.controls.target).add(direction);
    this.controls.update();
    this.render();
  }

  setPreviewExpanded(expanded) {
    this.orchestrator.dom.modalWrapper?.classList.toggle(
      'is-preview-expanded',
      Boolean(expanded),
    );
    requestAnimationFrame(() => {
      this.resizeRenderer();
      requestAnimationFrame(() => this.resizeRenderer());
    });
  }
}

window.BottleSceneController = BottleSceneController;
